import React, { useEffect, useState } from 'react';

import styles from './ResetPasswordForm.module.css';

import { Box, CircularProgress, Typography } from '@mui/material';
import { useInput } from '../../../customHook/useInput';
import { validatePassword } from '../../../helper/validate';
import { AuthService } from '../../../service/auth.service';
import zeplinColor from '../../../theme/Color';
import CustomAlert from '../../shared/CustomAlert/CustomAlert';
import CustomAlertContain from '../../shared/CustomAlert/CustomAlertContain';
import CustomButton from '../../shared/CustomButton/CustomButton';
import CustomPassword from '../../shared/CustomPassword/CustomPassword';

type ResetPasswordFormProps = {
  user: string;
};

const LoginReset = ({ user }: ResetPasswordFormProps) => {
  const MSLogin = sessionStorage.getItem('ms_login') === 'true';
  const token = sessionStorage.getItem('user-token') || '';
  let accessToken: any = {};
  if (token) {
    accessToken = JSON.parse(token);
  }
  const [mismatch, setMismatch] = useState(false);
  const [alert, setAlert] = useState('');
  const [error, setError] = useState(false);
  const [loader, setLoader] = useState(false);
  const [checkInProgress, setCheckInProgress] = useState(false);

  const {
    handleChange: handlePasswordChange,
    handleBlur: handlePasswordBlur,
    value: password,
    invalid: invalidPassword,
    didChange: didPasswordChange,
    clearInput: clearPassword,
  } = useInput('', (text) => validatePassword(text).valid, checkInProgress, setCheckInProgress);
  const {
    handleChange: handleNewPasswordChange,
    handleBlur: handleNewPasswordBlur,
    value: newPassword,
    invalid: invalidNewPassword,
    didChange: didNewPasswordChange,
    clearInput: clearNewPassword,
  } = useInput('', (text) => validatePassword(text).valid, checkInProgress, setCheckInProgress);

  const {
    handleChange: handleConfirmPasswordChange,
    handleBlur: handleConfirmPasswordBlur,
    value: confirmPassword,
    clearInput: clearConfirmPassword,
  } = useInput('', (text) => validatePassword(text).valid);

  const [check, setCheck] = useState<{
    valid: boolean;
    hasLowercase: boolean;
    hasUppercase: boolean;
    hasMinLength: boolean;
    hasSymbol: boolean;
    hasNumber: boolean;
  }>({
    valid: false,
    hasLowercase: false,
    hasUppercase: false,
    hasMinLength: false,
    hasSymbol: false,
    hasNumber: false,
  });

  const handleClose = () => {
    setAlert('');
  };
  useEffect(() => {
    setCheck(validatePassword(newPassword));
  }, [newPassword]);

  const valid = confirmPassword?.length && password?.length;
  const resetPassword = async () => {
    setLoader(true);
    try {
      if (confirmPassword === newPassword) {
        const res = await AuthService.changePassword({
          accessToken: accessToken.AccessToken,
          proposedPassword: newPassword,
          oldPassword: password,
        });
        console.log(res);
        if (res?.$metadata?.httpStatusCode === 200) {
          clearConfirmPassword();
          clearPassword();
          clearNewPassword();
          setCheckInProgress(true);
          setAlert('Password changed successfully');
        }
      } else {
        setMismatch(true);
      }
    } catch (err: any) {
      setError(true);
      setAlert('Error updating password');
    } finally {
      setLoader(false);
    }
  };

  const children = (
    <div className={styles['login-reset-field-input']}>
      {MSLogin && (
        <Box
          sx={{
            padding: '16px',
            borderRadius: '16px',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            backgroundColor: zeplinColor.Neutral3,
          }}
        >
          <img
            src={'/assets/information-circle.svg'}
            width={'20px'}
            height={'20px'}
            alt='information circle'
          />
          <Typography variant='body3' style={{ color: zeplinColor.Neutral9 }}>
            You’re signed in with Microsoft 365. Your password is managed
            <br /> through your Microsoft account.
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <Typography
          variant='heading3'
          style={{ color: MSLogin ? zeplinColor.Neutral6 : zeplinColor.BasicBlack }}
        >
          Change Password
        </Typography>
        <Typography
          variant='body3'
          style={{ color: MSLogin ? zeplinColor.Neutral6 : zeplinColor.Neutral9 }}
        >
          Keep your account safe with a new password.
        </Typography>
      </Box>
      <CustomPassword
        style={{ width: '100%', margin: 'auto' }}
        onBlur={handlePasswordBlur}
        onChange={handlePasswordChange}
        name='password'
        text={password}
        error={
          invalidPassword && didPasswordChange && checkInProgress ? 'Enter valid password' : ''
        }
        label='Old password'
        disabled={MSLogin ? true : false}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <CustomPassword
          style={{ width: '100%', margin: 'auto' }}
          onBlur={handleNewPasswordBlur}
          onChange={handleNewPasswordChange}
          name='password'
          text={newPassword}
          error={
            invalidNewPassword && didNewPasswordChange && checkInProgress
              ? 'Create valid password'
              : ''
          }
          label='New password'
          disabled={MSLogin ? true : false}
        />

        <div
          className={styles['login-password-rule']}
          style={{ color: MSLogin ? zeplinColor.Neutral6 : zeplinColor.Neutral9 }}
        >
          <div className={styles['validation-points']}>
            <img
              src={
                MSLogin
                  ? '/assets/dot-disabled.svg'
                  : check?.hasMinLength
                    ? '/assets/check.svg'
                    : '/assets/dot.svg'
              }
              width={20}
              height={20}
            />
            <Typography
              variant='body3'
              style={{ color: MSLogin ? zeplinColor.Neutral6 : zeplinColor.Neutral9 }}
            >
              At least 8 characters
            </Typography>
          </div>
          <div className={styles['validation-points']}>
            <img
              src={
                MSLogin
                  ? '/assets/dot-disabled.svg'
                  : check?.hasNumber
                    ? '/assets/check.svg'
                    : '/assets/dot.svg'
              }
              width={20}
              height={20}
            />
            <Typography
              variant='body3'
              style={{ color: MSLogin ? zeplinColor.Neutral6 : zeplinColor.Neutral9 }}
            >
              At least 1 number
            </Typography>
          </div>
          <div className={styles['validation-points']}>
            <img
              src={
                MSLogin
                  ? '/assets/dot-disabled.svg'
                  : check?.hasLowercase && check?.hasUppercase
                    ? '/assets/check.svg'
                    : '/assets/dot.svg'
              }
              width={20}
              height={20}
            />
            <Typography
              variant='body3'
              style={{ color: MSLogin ? zeplinColor.Neutral6 : zeplinColor.Neutral9 }}
            >
              At least 1 uppercase & 1 lowercase letter
            </Typography>
          </div>
          <div className={styles['validation-points']}>
            <img
              src={
                MSLogin
                  ? '/assets/dot-disabled.svg'
                  : check?.hasSymbol
                    ? '/assets/check.svg'
                    : '/assets/dot.svg'
              }
              width={20}
              height={20}
            />
            <Typography
              variant='body3'
              style={{ color: MSLogin ? zeplinColor.Neutral6 : zeplinColor.Neutral9 }}
            >
              At least 1 special character
            </Typography>
          </div>
        </div>
      </Box>
      <CustomPassword
        style={{ width: '100%', margin: 'auto' }}
        onBlur={handleConfirmPasswordBlur}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          handleConfirmPasswordChange(e);
          setMismatch(false);
        }}
        name='confirmPassword'
        text={confirmPassword}
        error={mismatch ? 'Password must match' : ''}
        label='Confirm new password'
        disabled={MSLogin ? true : false}
      />
      <Box sx={{ width: '100%' }}>
        <CustomButton
          onClick={resetPassword}
          variant='contained'
          disabled={(valid && !loader) || !MSLogin ? false : true}
          style={{ borderRadius: '14px', height: '48px', padding: '24px' }}
          textStyle={{
            color: (valid && !loader) || !MSLogin ? zeplinColor.Neutral1 : zeplinColor.Neutral8,
          }}
          text={'Change Password'}
        />
      </Box>

      <CustomAlert
        open={alert || loader ? true : false}
        handleClose={handleClose}
        message={
          <CustomAlertContain
            alert={loader ? 'Loading...' : alert}
            error={alert?.includes('successfully') || !alert || loader ? false : true}
            alertIcon={
              loader ? (
                <CircularProgress sx={{ color: zeplinColor.BasicBlack }} size={'24px'} />
              ) : alert?.includes('successfully') ? (
                <img src='/assets/checkmark-green.png' width={24} height={24} />
              ) : (
                <img src='/assets/error.svg' width={24} height={24} />
              )
            }
          />
        }
        error={alert?.includes('successfully') || !alert || loader ? false : true}
      />
    </div>
  );

  return <>{children}</>;
};

export default LoginReset;
